import {
  dashOf,
  inMark,
  inWord,
  LETTER_NAMES,
  LETTERS,
  MARK_LETTERS,
  middleOfMark,
  type LetterName,
  type LogoLayout,
  type MarkLetter,
  type Point,
  type StrokeKey,
} from "./drawing";

/**
 * The animation of the logo as a timeline (ADR 0015, 0016): "TAFO" is written, the word stands for a
 * moment, the T and the F are taken back, and the A and the O turn into the mark. Every part of the
 * drawing gets its own animation on one and the same clock. Times are milliseconds from the start.
 */

type Pace = "pen" | "run";

/** How a movement gathers and loses speed. */
const PACE = {
  /** A stroke: off quickly, landing softly. */
  pen: "cubic-bezier(0.5, 0, 0.2, 1)",
  /** The legs of the A: the pen runs on through the right foot. */
  run: "cubic-bezier(0.45, 0, 0.75, 0.8)",
  /** A stroke taken back. */
  back: "cubic-bezier(0.6, 0, 0.9, 0.6)",
  /** The turn: a calm start and a soft landing. */
  turn: "cubic-bezier(0.6, 0, 0.25, 1)",
} as const;

/** When each line is drawn. The letters overlap: the next one starts before the last is finished. */
const WRITE: Record<StrokeKey, readonly [from: number, to: number, pace: Pace]> = {
  T0: [100, 340, "pen"],
  T1: [220, 500, "pen"],
  A0: [340, 780, "run"],
  A1: [660, 920, "pen"],
  F0: [720, 980, "pen"],
  F1: [860, 1060, "pen"],
  F2: [950, 1130, "pen"],
  O0: [980, 1440, "pen"],
};

/** From here a letter is its plain outline, so the word stands exactly as the wordmark. */
const WRITTEN: Record<LetterName, number> = { T: 500, A: 920, F: 1130, O: 1440 };

/** The word has stood for 0.4 s. The T and the F are taken back as they were written, the last line first. */
const LEAVE = 1840;
const TAKEN_BACK: Partial<Record<StrokeKey, readonly [from: number, to: number]>> = {
  T1: [1840, 2000],
  T0: [1920, 2100],
  F2: [1840, 1950],
  F1: [1860, 1980],
  F0: [1900, 2060],
};

/** The A and the O turn into the mark; with the end of the turn the animation is over. */
const TURN = [1880, 2780] as const;
export const LOGO_ANIMATION_MS = TURN[1];

/** What stands next to the mark appears from here to the end: the O has passed its place by then. */
const BESIDE_FROM = 2450;

/** The turn is drawn through this many points; between two of them a letter moves in a straight line. */
const TURN_STEPS = 32;

/** One animation of one part of the drawing (`data-part`), as the Web Animations API takes it. */
export interface PartAnimation {
  part: string;
  keyframes: Keyframe[];
  timing: KeyframeAnimationOptions;
}

function polar(point: Point): { radius: number; angle: number } {
  const angle = Math.atan2(point.y, point.x);
  return { radius: Math.hypot(point.x, point.y), angle: angle < 0 ? angle + 2 * Math.PI : angle };
}

/**
 * The mark in the middle of the word: a quarter turn clockwise round the middle, the A from the left to
 * the top, the O from the right to the bottom. The O comes from far and is pulled in early, so it does not
 * swing wide.
 */
function quarterTurn(from: Point, to: Point, progress: number, pulledIn: boolean): Point {
  const start = polar(from);
  const end = polar(to);
  const pull = pulledIn ? 1 - (1 - progress) ** 1.5 : progress;
  const radius = start.radius + (end.radius - start.radius) * pull;
  const angle = start.angle + (end.angle - start.angle) * progress;
  return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
}

/**
 * The mark at the start of the word: the A and the O both come from the right. They first part, the A up
 * and the O down, and are at their height at three quarters of the way; then they slide together to the
 * left. Nothing swings above or below the mark, so the logo keeps to the height of its bar.
 */
function apartThenTogether(from: Point, to: Point, progress: number): Point {
  const sideways = Math.cos((progress * Math.PI) / 2);
  const upOrDown = Math.sin((Math.min(1, progress / 0.75) * Math.PI) / 2);
  return { x: to.x + (from.x - to.x) * sideways, y: from.y + (to.y - from.y) * upOrDown };
}

/**
 * How far a letter of the mark is from its place in the mark when the turn is at `progress` (0: where it
 * stands in the word, 1: in the mark). On the way the A and the O never come closer to each other than
 * they stand in the mark.
 */
export function onTheTurn(name: MarkLetter, progress: number, layout: LogoLayout): Point {
  const letter = LETTERS[name];
  const centre = middleOfMark(layout);
  const word = inWord(name, layout);
  const mark = inMark(name, layout);
  // the middle of the letter, seen from the middle of the mark
  const from = { x: word.x + letter.width / 2 - centre.x, y: word.y + letter.height / 2 - centre.y };
  const to = { x: mark.x + letter.width / 2 - centre.x, y: mark.y + letter.height / 2 - centre.y };
  const now =
    layout === "middle" ? quarterTurn(from, to, progress, name === "O") : apartThenTogether(from, to, progress);
  return { x: now.x - to.x, y: now.y - to.y };
}

/** A length for a style: two decimals are a hundredth of a pixel of the logo. */
function px(value: number): string {
  return `${Math.round(value * 100) / 100}px`;
}

/** The moment `ms` as the place on the clock of the whole animation (0 to 1). */
function at(ms: number): number {
  return ms / LOGO_ANIMATION_MS;
}

/** Shown or hidden from each moment on; a value holds until the next moment. */
function shown(...moments: readonly (readonly [ms: number, visible: boolean])[]): Keyframe[] {
  return moments.map(([ms, visible]) => ({
    offset: at(ms),
    visibility: visible ? "visible" : "hidden",
    easing: "step-end",
  }));
}

/**
 * What stands next to the mark at the start of a bar (`data-part="beside"`): not there while the word is,
 * then it appears. There is no last moment in this animation: it ends as the style sheet shows the part.
 */
export function besideTheMark(): PartAnimation {
  return {
    part: "beside",
    keyframes: [
      { offset: 0, opacity: 0 },
      { offset: at(BESIDE_FROM), opacity: 0 },
    ],
    timing: { duration: LOGO_ANIMATION_MS },
  };
}

/** Every animation of the logo. They all start together and end at `LOGO_ANIMATION_MS`. */
export function logoTimeline(layout: LogoLayout): PartAnimation[] {
  const whole: KeyframeAnimationOptions = { duration: LOGO_ANIMATION_MS };
  const parts: PartAnimation[] = [];

  for (const name of LETTER_NAMES) {
    // the lines of the letter: drawn, and for the T and the F taken back again
    for (const stroke of LETTERS[name].strokes) {
      const [from, to, pace] = WRITE[stroke.key];
      const back = TAKEN_BACK[stroke.key];
      const nothing = px(dashOf(stroke));
      const keyframes: Keyframe[] = [
        { offset: 0, strokeDashoffset: nothing },
        { offset: at(from), strokeDashoffset: nothing, easing: PACE[pace] },
        { offset: at(to), strokeDashoffset: px(0) },
      ];
      if (back !== undefined) {
        keyframes.push(
          { offset: at(back[0]), strokeDashoffset: px(0), easing: PACE.back },
          { offset: at(back[1]), strokeDashoffset: nothing },
        );
      }
      keyframes.push({ offset: 1, strokeDashoffset: back === undefined ? px(0) : nothing });
      parts.push({ part: stroke.key, keyframes, timing: whole });
    }

    // the lines make place for the outline once the letter is written
    const leaves = name === "T" || name === "F";
    parts.push({
      part: `lines-${name}`,
      keyframes: leaves
        ? shown([0, true], [WRITTEN[name], false], [LEAVE, true], [LOGO_ANIMATION_MS, true])
        : shown([0, true], [WRITTEN[name], false], [LOGO_ANIMATION_MS, false]),
      timing: whole,
    });
    parts.push({
      part: `outline-${name}`,
      keyframes: shown(
        [0, false],
        [WRITTEN[name], true],
        [leaves ? LEAVE : TURN[0], false],
        [LOGO_ANIMATION_MS, false],
      ),
      timing: whole,
    });
  }

  // the mark takes over from the A and the O of the word when the turn starts, and is moved to its place
  for (const name of MARK_LETTERS) {
    parts.push({
      part: `mark-${name}`,
      keyframes: shown([0, false], [TURN[0], true], [LOGO_ANIMATION_MS, true]),
      timing: whole,
    });
    const path: Keyframe[] = [];
    for (let step = 0; step <= TURN_STEPS; step++) {
      const { x, y } = onTheTurn(name, step / TURN_STEPS, layout);
      path.push({ transform: `translate(${px(x)}, ${px(y)})` });
    }
    parts.push({
      part: `mark-${name}`,
      keyframes: path,
      timing: { delay: TURN[0], duration: TURN[1] - TURN[0], easing: PACE.turn, fill: "backwards" },
    });
  }
  return parts;
}
